import { MESSAGES } from "@/constants";
import { AxiosError, isAxiosError } from "axios";
import type { ApiErrorEnvelope, ApiErrorIssue, AppError } from "@/types/error";

/**
 * Parses structured error responses from the backend API.
 * 
 * Handles four different error response formats:
 * 1. Array format: `{ errors: [{ title, message, description, code }] }`
 * 2. Object format: `{ errors: { title, message, description, code } }`
 * 3. Security format: `{ error: { message, error_code, details: { reason, resource }, type } }`
 *    — the shape the backend returns for permission/security errors (e.g. 403s).
 *    Only matches when `error` is an object; legacy bodies carrying `error` as a
 *    string fall through to the branches below.
 * 4. Direct format: `{ title, message, description, code }`
 *
 * @param data - Unknown data from error response
 * @returns Parsed error object with title, message, description, and code, or null if parsing fails
 */
const parseStructuredError = (data: unknown) => {
  // Type guard: ensure data is an object before casting
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  
  const errorBody = data as {
    errors?: Array<{ title?: string; message?: string; description?: string; code?: string }> | { title?: string; message?: string; description?: string; code?: string };
    error?: string | { message?: string; error_code?: string; details?: { reason?: string; resource?: string }; type?: string };
    title?: string;
    message?: string;
    description?: string;
    code?: string;
  };
  
  // Handle array format: { errors: [{ title, message, description, code }] }
  if (Array.isArray(errorBody?.errors) && errorBody.errors.length > 0) {
    return errorBody.errors[0];
  }
  
  // Handle object format: { errors: { code, message } }
  if (errorBody?.errors && !Array.isArray(errorBody.errors)) {
    return errorBody.errors;
  }
  
  // Handle security format: { error: { message, error_code, details: { reason } } }
  // Guarded on `error` being an object — legacy bodies carry `error` as a string.
  const securityError = errorBody?.error;
  if (typeof securityError === 'object' && securityError !== null) {
    if (securityError.message || securityError.error_code) {
      return {
        title: undefined,
        message: securityError.message,
        description: securityError.details?.reason || securityError.message,
        code: securityError.error_code,
      };
    }
  }

  // Handle direct format: { title, message, description, code }
  if (errorBody?.code || errorBody?.title || errorBody?.message) {
    const { title, message, description, code } = errorBody;
    return { title, message, description, code };
  }
  
  return null;
};

export const getErrorObject = (error: Error | AxiosError): AppError => {
    // Check if this is an AxiosError
    if (!isAxiosError(error)) {
      // Handle plain Error objects
      return {
        title: 'Error',
        message: error.message || 'An unexpected error occurred',
        description: error.message || 'An unexpected error occurred',
        code: MESSAGES.ERROR_CODES.UNKNOWN_ERROR,
        originalError: error,
      };
    }

    // Now we know it's an AxiosError
    // Check if we have a response (server successfully responded with status code)
    if (error.response) {
      const status = error.response.status;
      
      // Handle 5xx server errors
      if (status >= 500 && status < 600) {
        const structuredError = parseStructuredError(error.response?.data);
        if(structuredError){
          return {
            title: structuredError.title || 'Server error',
            message: structuredError.message || 'Something went wrong. iQ will be back up shortly.',
            description: structuredError.description || 'Something went wrong. iQ will be back up shortly.',
            code: structuredError.code || MESSAGES.ERROR_CODES.SERVER_ERROR,
            status,
            originalError: error,
          };
        }
        return {
          title: 'Server error',
          message: 'Something went wrong. iQ will be back up shortly.',
          description: error.response?.statusText || 'Something went wrong. iQ will be back up shortly.',
          code: MESSAGES.ERROR_CODES.SERVER_ERROR,
          status,
          originalError: error,
        };
      }

      // Handle other HTTP errors (4xx, etc.) with response data
      const structuredError = parseStructuredError(error.response?.data);
      if(structuredError){
        return {
          title: structuredError.title || 'Error',
          message: structuredError.message || error.message,
          description: structuredError.description || error.message,
          code: structuredError.code || MESSAGES.ERROR_CODES.UNKNOWN_ERROR,
          status,
          originalError: error,
        };
      }
      
      return {
        title: 'Error',
        message: error.message || 'An unexpected error occurred',
        description: error.response?.statusText || error.message || 'An unexpected error occurred',
        code: MESSAGES.ERROR_CODES.UNKNOWN_ERROR,
        status,
        originalError: error,
      };
    }

    // No response - could be network issue OR server error that browser treats as network error
    // Check if browser is actually offline (true connection loss)
    // Not adding ERR_NETWORK code which indicates network issues as it's not working as expected
    const isBrowserOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const errorMessage = error.message?.toLowerCase() || "";
    const isLikelyConnectionIssue =
      isBrowserOffline ||
      error.code === "ECONNABORTED" ||
      error.code === "ERR_INTERNET_DISCONNECTED" ||
      errorMessage.includes("failed to fetch") ||
      errorMessage.includes("dns") ||
      errorMessage.includes("connection refused") ||
      errorMessage.includes("connection reset") ||
      errorMessage.includes("connection timed out") ||
      errorMessage.includes("network request failed");

    if (isLikelyConnectionIssue) {
      // Browser is offline - definitely a connection issue
      return {
        title: 'Connection error',
        message: 'Unable to reach iQ. Please check your connection or try again shortly.',
        description: 'Unable to reach iQ. Please check your connection or try again shortly.',
        code: MESSAGES.ERROR_CODES.NETWORK,
        originalError: error,
      };
    }

    // Browser is online but no response - treat as server error
    // (500 errors often appear as "Network Error" with no response)
    return {
      title: 'Server error',
      message: 'Something went wrong. iQ will be back up shortly.',
      description: 'Something went wrong. iQ will be back up shortly.',
      code: MESSAGES.ERROR_CODES.SERVER_ERROR,
      originalError: error,
    };
}

/**
 * Extracts the backend's own human-readable message from an error, when it carries a
 * structured response body (see {@link parseStructuredError} for the formats handled).
 *
 * Lets views prefer the backend's copy over a hardcoded fallback, e.g.
 * `getBackendErrorMessage(query.error) ?? "You don't have access to this dashboard."`
 *
 * @param error - Unknown error (an AxiosError, a plain Error, or anything else)
 * @returns The backend's message, or undefined if the error carries no parsable body
 */
export const getBackendErrorMessage = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) {
    return undefined;
  }
  return parseStructuredError(error.response?.data)?.message;
};

/**
 * The reason line for an error toast the caller shows itself (its request opts out
 * of the global handler with `skipGlobalErrorHandler`).
 *
 * Uses the backend's own message when the response carries a non-empty string, otherwise
 * `fallback` — never axios's "Request failed with status code …". A trailing full
 * stop is dropped so the caller can append its own sentence after it.
 *
 * @param error - Unknown error (an AxiosError, a plain Error, or anything else)
 * @param fallback - User-facing text to use when the backend sent no message
 * @returns The reason, without a trailing full stop
 */
export const getToastErrorReason = (error: unknown, fallback: string): string => {
  const message = getBackendErrorMessage(error);
  const reason = typeof message === "string" ? message.trim().replace(/[.\s]+$/, "") : "";
  return reason || fallback.trim().replace(/[.\s]+$/, "");
};

const optionalString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

/** Keeps only issues that carry a string `message`; anything else is dropped. */
const parseIssues = (value: unknown): ApiErrorIssue[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): ApiErrorIssue[] => {
    if (typeof item !== "object" || item === null) return [];
    const { message, reason_code } = item as { message?: unknown; reason_code?: unknown };
    if (typeof message !== "string" || !message.trim()) return [];
    return [{ message, reason_code: optionalString(reason_code) }];
  });
};

/**
 * Reads the backend's standard REST error envelope
 * (`{ type, status_code, error_code, title, content, request_id, issues? }`)
 * off an axios error. The body is untrusted: every field is type-checked and a
 * wrong-typed field is omitted rather than passed through.
 *
 * @param error - Anything a request can reject with.
 * @returns The HTTP status (undefined when no response arrived, e.g. offline)
 *   and the parsed envelope, or `null` when `error` is not an axios error.
 *
 * @example
 * parseApiErrorEnvelope(axios422)
 * // → { status: 422, envelope: { error_code: "INSTRUCTION_REJECTED", content: "…", issues: [{ message: "…" }] } }
 *
 * @example
 * parseApiErrorEnvelope(new Error("boom")) // → null
 */
export const parseApiErrorEnvelope = (
  error: unknown,
): { status: number | undefined; envelope: ApiErrorEnvelope } | null => {
  if (!isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  const body = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  return {
    status: error.response?.status,
    envelope: {
      error_code: optionalString(body.error_code),
      content: optionalString(body.content),
      request_id: optionalString(body.request_id),
      issues: parseIssues(body.issues),
    },
  };
};
